import type { ReactNode } from 'react'
import { createElement, useMemo } from 'react'
import { type AccessDenialReason, AccessDenied } from 'v2/pages/misc/access-denied'

export type { AccessDenialReason }

export interface PageAccess {
  allowed: boolean
  reason: AccessDenialReason
  /** Render this when `allowed` is false. */
  fallback: ReactNode
}

/**
 * The one place a page decides whether the signed-in user may open it.
 *
 * Pass the page's own RBAC result (pages already compute it, e.g. via
 * `useSettingsAccess`); this hook owns what happens when the answer is no, so
 * every gated page denies the same way instead of hand-rolling a redirect or
 * falling through to a 404.
 *
 *   const { isAdmin } = useSettingsAccess()
 *   const { allowed, fallback } = usePageAccess(isAdmin)
 *   if (!allowed) return fallback
 *
 * Route-level gates (`notAllowed` / `hasAccess` in the route config) are handled
 * for you in `generateRoutes` and do not need this hook.
 *
 * NOT for "this id isn't on your account". A missing entity must keep returning
 * the 404 so we never confirm that another tenant's brand, campaign or call
 * exists. This hook is for pages, whose existence is the same for every org and
 * therefore not sensitive.
 */
export const usePageAccess = (
  allowed: boolean,
  reason: AccessDenialReason = 'unknown',
): PageAccess =>
  useMemo(
    () => ({
      allowed,
      reason,
      fallback: allowed ? null : createElement(AccessDenied, { reason }),
    }),
    [allowed, reason],
  )
